"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Download, Info, LayoutGrid, List as ListIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip as InfoTooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { buildDividendCalendarIcs } from "@/lib/dividend-tracker/ics-export";
import { formatCurrency, formatCompactCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

function downloadDividendCalendarIcs(entries: DividendCalendarEntry[]) {
  const blob = new Blob([buildDividendCalendarIcs(entries)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "erikonomie-dividendenkalender.ics";
  link.click();
  URL.revokeObjectURL(url);
}

export interface DividendCalendarEntry {
  symbol: string;
  name: string;
  logoUrl?: string;
  /** ISO-Datum. */
  date: string;
  /** Brutto-Gesamtbetrag in der Notierungswaehrung = Betrag je Aktie x Stückzahl. */
  amount: number;
  currency: string;
  /** Netto-EUR-Betrag (dt. Kapitalertragsteuer-Näherung, flacher 26,375%-Satz OHNE
   * Sparerpauschbetrag, siehe computeNetAmount - Nutzer-Feedback 2026-07-18: der
   * Freibetrag gilt bewusst nur bei der Jahres-Gesamtberechnung/-Hochrechnung, nicht
   * hier) - undefined, wenn der EUR-Kurs für `currency` nicht abrufbar war; Anzeige
   * fällt dann auf den Brutto-Betrag in der Notierungswährung zurück statt einen
   * falschen Wert zu erfinden. */
  amountEurNet?: number;
  /** true nur für das von Yahoo tatsächlich bestätigte nächste Ex-Datum. */
  confirmed: boolean;
}

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
/** Deckt sich mit CALENDAR_WINDOW_MONTHS in lib/dividend-tracker/load-projection.ts - die
 * Monatsnavigation im Grid darf nicht weiter reichen als die Daten tatsächlich laden. */
const CALENDAR_WINDOW_MONTHS = 12;

function startOfMonthUtc(d: Date): { year: number; month: number } {
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
}

function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

/**
 * Monats-Kalenderraster (Feedback-Runde 19 C/8, 2026-07-28, bewusste Richtungsänderung
 * ggü. der vorherigen Liste-statt-Grid-Entscheidung) - pro Tag sichtbar, welche Aktie wie
 * viel Netto-Dividende zahlt, plus eine Monats-Summe im Kopf. Zeigt nur Tage aus `entries`
 * innerhalb des gewählten Monats; Navigation ist auf das von loadDividendOutlook() geladene
 * 12-Monats-Fenster begrenzt (siehe CALENDAR_WINDOW_MONTHS oben).
 */
function MonthGrid({
  entries,
  year,
  month,
}: {
  entries: DividendCalendarEntry[];
  year: number;
  month: number;
}) {
  const byDay = useMemo(() => {
    const map = new Map<number, DividendCalendarEntry[]>();
    for (const entry of entries) {
      const d = new Date(entry.date);
      if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month) continue;
      const list = map.get(d.getUTCDate()) ?? [];
      list.push(entry);
      map.set(d.getUTCDate(), list);
    }
    return map;
  }, [entries, year, month]);

  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  // getUTCDay(): 0=So..6=Sa - auf 0=Mo..6=So umgerechnet (deutsche Wochendarstellung).
  const firstWeekday = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
  const cells: (number | null)[] = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();
  const isCurrentMonth = today.getUTCFullYear() === year && today.getUTCMonth() === month;

  return (
    <div className="grid grid-cols-7 gap-1">
      {WEEKDAY_LABELS.map((w) => (
        <div key={w} className="text-center text-[0.65rem] text-muted-foreground">
          {w}
        </div>
      ))}
      {cells.map((day, i) => {
        if (day === null) return <div key={`empty-${i}`} />;
        const dayEntries = byDay.get(day) ?? [];
        const hasGap = dayEntries.some((e) => e.amountEurNet === undefined);
        const dayTotal = dayEntries.reduce((sum, e) => sum + (e.amountEurNet ?? 0), 0);
        const isToday = isCurrentMonth && today.getUTCDate() === day;
        const cell = (
          <div
            className={cn(
              "flex min-h-[4.5rem] flex-col gap-0.5 rounded-md border border-hairline p-1",
              dayEntries.length > 0 ? "bg-positive/5" : undefined,
              isToday && "ring-1 ring-primary",
            )}
          >
            <span className="text-[0.65rem] text-muted-foreground">{day}</span>
            {dayEntries.slice(0, 2).map((entry, idx) => (
              <div key={idx} className="flex min-w-0 items-center gap-1">
                {entry.logoUrl && (
                  <Image
                    src={entry.logoUrl}
                    alt=""
                    width={16}
                    height={16}
                    unoptimized
                    className="h-4 w-4 shrink-0 rounded-full border border-hairline bg-white object-contain"
                  />
                )}
                <span className="truncate text-[0.65rem] font-tabular font-medium text-positive">
                  {entry.amountEurNet !== undefined
                    ? `+${formatCompactCurrency(entry.amountEurNet, "EUR")}`
                    : `+${formatCompactCurrency(entry.amount, entry.currency)}`}
                </span>
              </div>
            ))}
            {dayEntries.length > 2 && (
              <span className="text-[0.6rem] text-muted-foreground">+{dayEntries.length - 2} weitere</span>
            )}
            {dayEntries.length > 0 && (dayTotal > 0 || hasGap) && (
              <span className="mt-auto truncate text-[0.65rem] font-tabular font-semibold text-ink">
                {formatCurrency(dayTotal, "EUR")}
                {hasGap && "*"}
              </span>
            )}
          </div>
        );
        if (dayEntries.length === 0) return <div key={day}>{cell}</div>;
        return (
          <InfoTooltip key={day}>
            <TooltipTrigger asChild>{cell}</TooltipTrigger>
            <TooltipContent side="top" className="max-w-72 flex-col items-stretch gap-1.5 py-2">
              {dayEntries.map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 text-xs">
                  <span className="min-w-0 truncate">
                    {entry.name}
                    {!entry.confirmed && " · geschätzt"}
                  </span>
                  <span className="shrink-0 font-tabular font-medium">
                    {formatCurrency(entry.amount, entry.currency)} brutto
                    {entry.amountEurNet !== undefined && ` · ${formatCurrency(entry.amountEurNet, "EUR")} netto`}
                  </span>
                </div>
              ))}
            </TooltipContent>
          </InfoTooltip>
        );
      })}
    </div>
  );
}

function ViewToggle({ view, onChange }: { view: "grid" | "list"; onChange: (v: "grid" | "list") => void }) {
  const options: { key: "grid" | "list"; label: string; Icon: typeof LayoutGrid }[] = [
    { key: "grid", label: "Kalender", Icon: LayoutGrid },
    { key: "list", label: "Liste", Icon: ListIcon },
  ];
  return (
    <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          aria-pressed={view === o.key}
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
            view === o.key ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
          )}
        >
          <o.Icon className="h-3 w-3" />
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Personalisierter Dividenden-Kalender (backlog.md -> Feedback-Runde 19 C/8). Zwei
 * Ansichten: das neue Monats-Kalenderraster (Standard) und die ursprüngliche
 * chronologische Liste weiterhin als kompakte Alternative (Erik wollte laut backlog.md
 * primär das Grid, die Liste war aber schon vorher da und bleibt bewusst erhalten statt
 * ersatzlos zu verschwinden). Nur das jeweils nächste Ex-Datum je Aktie ist bestätigt,
 * alle weiteren Termine sind erkennbar als Schätzung markiert (siehe
 * lib/portfolio/dividend-calendar.ts). Beträge seit Nutzer-Feedback 2026-07-18 netto in
 * EUR statt brutto in der jeweiligen Notierungswährung (gleiche Steuer-Näherung wie der
 * Dividenden-Tracker).
 */
export function DividendCalendarCard({ entries }: { entries: DividendCalendarEntry[] }) {
  const [view, setView] = useState<"grid" | "list">("grid");
  const now = useMemo(() => new Date(), []);
  const minMonth = useMemo(() => startOfMonthUtc(now), [now]);
  const maxMonth = useMemo(() => addMonths(minMonth.year, minMonth.month, CALENDAR_WINDOW_MONTHS - 1), [minMonth]);
  const [cursor, setCursor] = useState(minMonth);

  if (entries.length === 0) return null;

  let thisMonthTotalEur = 0;
  let thisMonthHasEurGap = false;
  for (const entry of entries) {
    const d = new Date(entry.date);
    if (d.getUTCFullYear() !== now.getUTCFullYear() || d.getUTCMonth() !== now.getUTCMonth()) continue;
    if (entry.amountEurNet === undefined) {
      thisMonthHasEurGap = true;
      continue;
    }
    thisMonthTotalEur += entry.amountEurNet;
  }

  // Laufende Jahressumme (Nutzwert-Katalog: "laufende 'Gesamt dieses Jahr'-Summe im
  // Dividendenkalender ergaenzen") - Summe ALLER Kalendereintraege im laufenden Kalenderjahr
  // (bereits bestaetigt vergangene UND geschaetzte kommende, `entries` deckt beides ab),
  // nicht nur ein einzelner Monat - gibt einen Gesamtueberblick, ohne 12 Monate im Kopf
  // aufsummieren zu muessen. Unabhaengig von Grid-/Listenansicht sichtbar (Kopfbereich).
  let thisYearTotalEur = 0;
  let thisYearHasEurGap = false;
  for (const entry of entries) {
    const d = new Date(entry.date);
    if (d.getUTCFullYear() !== now.getUTCFullYear()) continue;
    if (entry.amountEurNet === undefined) {
      thisYearHasEurGap = true;
      continue;
    }
    thisYearTotalEur += entry.amountEurNet;
  }

  let cursorMonthTotalEur = 0;
  let cursorMonthHasEurGap = false;
  for (const entry of entries) {
    const d = new Date(entry.date);
    if (d.getUTCFullYear() !== cursor.year || d.getUTCMonth() !== cursor.month) continue;
    if (entry.amountEurNet === undefined) {
      cursorMonthHasEurGap = true;
      continue;
    }
    cursorMonthTotalEur += entry.amountEurNet;
  }

  const cursorIndex = cursor.year * 12 + cursor.month;
  const atMin = cursorIndex <= minMonth.year * 12 + minMonth.month;
  const atMax = cursorIndex >= maxMonth.year * 12 + maxMonth.month;
  const monthLabel = new Date(Date.UTC(cursor.year, cursor.month, 1)).toLocaleDateString("de-DE", {
    month: "long",
    year: "numeric",
  });

  return (
    <Card className="reveal">
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <h3 className="flex items-center gap-1.5 font-serif text-xl text-ink">
              Dividenden-Kalender
              <InfoTooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Erklärung: Dividenden-Kalender" className="text-primary hover:text-primary/80">
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                  Nur das jeweils nächste Zahldatum je Aktie ist von Yahoo bestätigt. Weitere
                  Termine sind aus dem historischen Zahlungsrhythmus geschätzt, Beträge
                  unverändert von der letzten bekannten Zahlung fortgeschrieben. Alle Beträge
                  netto in EUR, nach deutscher Kapitalertragsteuer-Näherung (26,375 %) - ohne
                  Sparerpauschbetrag, der zählt nur bei der Jahres-Gesamtübersicht und der
                  Mehrjahres-Hochrechnung. Keine Steuerberatung.
                </TooltipContent>
              </InfoTooltip>
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-muted-foreground">Alle Beträge netto in EUR</span>
              {thisYearTotalEur > 0 && (
                <span className="text-xs text-muted-foreground">
                  Gesamt {now.getUTCFullYear()}:{" "}
                  <span className="font-tabular font-medium text-ink">
                    {formatCurrency(thisYearTotalEur, "EUR")}
                    {thisYearHasEurGap && "*"}
                  </span>
                </span>
              )}
              <button
                type="button"
                onClick={() => downloadDividendCalendarIcs(entries)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                Als .ics exportieren
              </button>
            </div>
          </div>
          <ViewToggle view={view} onChange={setView} />
        </div>

        {view === "grid" ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c.year, c.month, -1))}
                disabled={atMin}
                aria-label="Vorheriger Monat"
                className="rounded-full bg-primary/10 p-2 text-primary hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex flex-col items-center">
                <span className="text-sm font-medium capitalize text-ink">{monthLabel}</span>
                <span className="text-xs text-muted-foreground">Summe (netto)</span>
                <span className="font-tabular text-lg font-semibold text-primary">
                  {formatCurrency(cursorMonthTotalEur, "EUR")}
                  {cursorMonthHasEurGap && "*"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c.year, c.month, 1))}
                disabled={atMax}
                aria-label="Nächster Monat"
                className="rounded-full bg-primary/10 p-2 text-primary hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <MonthGrid entries={entries} year={cursor.year} month={cursor.month} />
            {cursorMonthHasEurGap && (
              <p className="text-xs text-muted-foreground">* unvollständig, EUR-Kurs für mind. eine Position nicht verfügbar</p>
            )}
          </>
        ) : (
          <>
            {thisMonthTotalEur > 0 && (
              <p className="text-xs text-muted-foreground">
                Diesen Monat erwartet (netto):{" "}
                <span className="font-tabular font-medium text-ink">{formatCurrency(thisMonthTotalEur, "EUR")}</span>
                {thisMonthHasEurGap && " (unvollständig, EUR-Kurs für mind. eine Position nicht verfügbar)"}
              </p>
            )}
            <ul className="flex flex-col gap-2">
              {entries.slice(0, 12).map((entry, i) => (
                <li key={`${entry.symbol}-${entry.date}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-2">
                    {entry.logoUrl && (
                      <Image
                        src={entry.logoUrl}
                        alt=""
                        width={20}
                        height={20}
                        unoptimized
                        className="h-5 w-5 shrink-0 rounded-full border border-hairline bg-white object-contain p-0.5"
                      />
                    )}
                    <span className="text-ink-secondary">
                      {entry.name}
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {new Date(entry.date).toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}
                        {!entry.confirmed && " · geschätzt"}
                      </span>
                    </span>
                  </span>
                  <span className="shrink-0 font-tabular font-medium text-positive">
                    {entry.amountEurNet !== undefined
                      ? `+${formatCurrency(entry.amountEurNet, "EUR")} netto`
                      : `+${formatCurrency(entry.amount, entry.currency)} brutto`}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-xs text-muted-foreground">
          Rein deskriptiv, keine Zusage – Dividenden können gekürzt oder gestrichen werden.
        </p>
      </CardContent>
    </Card>
  );
}
